import { useEffect, useMemo, useState } from "react"
import { getCompanyMembers, getInvitations } from "src/services/api/companyMembers"
import type { CompanyMember, CompanyMembersSortBy } from "src/services/api/companyMembers"
import type { Role } from "src/UserService"
import type { Order } from "src/components/table/SortableTableHeaderCell"
import { Community } from "@models/user"
import { filterMembersByQuery, filterMembersByRoles, sortMembersByField } from "./companyMembersUtils"

export interface UseCompanyMembersFetchParams {
  companyId: string | undefined
  page: number
  rowsPerPage: number
  orderBy: CompanyMembersSortBy
  order: Order
  query: string
  roles: Role[]
  communityIds: string[]
  refreshKey: number
  userCommunities: Community[]
}

function filterMembers(members: CompanyMember[], query: string, roles: Role[], communityIds: string[]): CompanyMember[] {
  let result = filterMembersByQuery(filterMembersByRoles(members, roles), query)
  if (communityIds.length > 0) {
    result = result.filter((m) => m.communities.some((c) => communityIds.includes(c.communityId)))
  }
  return result
}

export function useCompanyMembersFetch({
  companyId,
  page,
  rowsPerPage,
  orderBy,
  order,
  query,
  roles,
  communityIds,
  refreshKey,
  userCommunities
}: UseCompanyMembersFetchParams) {
  const [allMembers, setAllMembers] = useState<CompanyMember[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!companyId) {
      setLoading(false)
      setAllMembers([])
      return
    }
    setLoading(true)
    Promise.all([getCompanyMembers(companyId, { limit: 9999, offset: 0 }), getInvitations(companyId, userCommunities)])
      .then(([wflData, portalData]) => {
        const seen = new Set<string>()
        const merged: CompanyMember[] = []
        for (const m of [...wflData.results, ...portalData]) {
          if (!seen.has(m.email)) {
            seen.add(m.email)
            merged.push(m)
          }
        }
        setAllMembers(merged)
      })
      .catch(() => setAllMembers([]))
      .finally(() => setLoading(false))
  }, [companyId, refreshKey, orderBy, order, query, roles, communityIds])

  const filtered = useMemo(
    () => filterMembers(allMembers, query, roles, communityIds),
    [allMembers, query, roles, communityIds]
  )
  const sorted = useMemo(() => sortMembersByField(filtered, orderBy, order), [filtered, orderBy, order])
  const members = useMemo(() => sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage), [sorted, page, rowsPerPage])

  return { members, totalCount: filtered.length, loading }
}
